import { type Query } from '@tanstack/react-query';

/**
 * Extracts params from a query key, assuming the structure is [key, params, ...]
 */
const getParams = (query: Query<unknown, Error, unknown, readonly unknown[]>): unknown => {
  const [, params] = query.queryKey;
  return params;
};

/**
 * Checks if params is an object with the specified property matching the value
 */
const hasMatchingParam = <T extends string>(
  params: unknown,
  property: T,
  value: unknown
): params is Record<T, unknown> => {
  return (
    params !== null &&
    params !== undefined &&
    typeof params === 'object' &&
    property in params &&
    (params as Record<string, unknown>)[property] === value
  );
};

/**
 * Creates a predicate function that matches queries where the params object
 * has a specific property matching a given value.
 *
 * @example
 * ```ts
 * await queryClient.invalidateQueries({
 *   queryKey: [QUERY_KEY.getUserSources],
 *   predicate: matchParam('userId', variables.userId),
 * });
 * ```
 */
export const matchParam =
  <T extends string>(property: T, value: unknown) =>
  (query: Query<unknown, Error, unknown, readonly unknown[]>): boolean => {
    const params = getParams(query);
    return hasMatchingParam(params, property, value);
  };

/**
 * Creates a predicate function that matches queries where the params object
 * has multiple properties matching given values.
 *
 * @example
 * ```ts
 * await queryClient.invalidateQueries({
 *   queryKey: [QUERY_KEY.getUserSources],
 *   predicate: matchParams({ userId: variables.userId, role: 'admin' }),
 * });
 * ```
 */
export const matchParams =
  <T extends Record<string, unknown>>(matches: T) =>
  (query: Query<unknown, Error, unknown, readonly unknown[]>): boolean => {
    const params = getParams(query);
    if (!params || typeof params !== 'object') return false;

    return Object.entries(matches).every(([key, value]) => {
      return key in params && (params as Record<string, unknown>)[key] === value;
    });
  };
